// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Settings (M3-37, 08 §3).
//
// The anonymous half: appearance and graphics. Identity, security and cloud-synced preferences
// are `/account` and need M4's accounts — they are not stubbed here, because a settings page
// listing controls that do nothing is worse than one that does not list them.
//
// **Selection rather than typing**, per the conventions: every control on this page is a choice
// among named options, because there is no setting here whose value a player could type more
// accurately than they could pick it.
//
// The graphics control is a genuine override, not a preview. It is the same `TierChoice` the
// renderer and the audio voice pool read, it persists, and "Automatic" hands the decision back to
// the measurement rather than freezing whatever the governor last fell to.
//
// **Nudging is here because "offered once" must not mean "decided forever" (M3-34).** 08 §5.2
// asks for the motion offer to be made once and to decline gracefully; a player who tapped "No
// thanks" on their first ball and later wants to try it has nowhere to go if the only way to
// answer is an offer that has already been made.
//
// It is shown on exactly the devices the offer is made on, through the same function — not on
// "has a sensor". A desktop browser passes that test, and a setting that exists where the offer
// never appears says the feature is available here and this machine is failing to provide it.
//
// **The comfort controls are here and not buried (M3-40).** 01 §8.4 asks for an in-game "reduce
// flashing" setting alongside the system preference, and for the motion intensity to be a taste
// control rather than an on/off. Both sit above the graphics tier on purpose: a player who has
// come here because the machine is uncomfortable should not have to read past a performance
// setting to find the one they wanted.
//
// **What this browser is keeping is shown, and can be thrown away (M3-39).** An anonymous player
// creates no account and no server record, so everything about them is here — and a page that
// stores things on somebody's device without telling them, and with no way to clear it, is the
// thing the rest of this product's storage policy exists to avoid.

import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { renderTier } from '../tier'
import { THEME_CHOICES, type ThemeChoice, type ThemeController } from '../theme'
import { RENDER_TIERS, type RenderTier } from '../../world/tiers'
import {
  forgetTouchZones,
  hasSeenTouchZones,
  readMotionChoice,
  writeMotionChoice,
  type MotionChoice,
} from '../firstRun'
import { forgetOnboarding, readOnboarding } from '../onboarding'
import { OnboardingStrip } from '../components/OnboardingStrip'
import { browserMotionHost, requestMotionPermission, shouldOfferMotionNudge } from '../../input/motion'
import { LocalHistory, MAX_REPLAYS } from '../../storage/history'
import {
  FLASHING_CHOICES,
  readFlashingChoice,
  readHaptics,
  readMotionIntensity,
  writeFlashingChoice,
  writeHaptics,
  writeMotionIntensity,
  type FlashingChoice,
} from '../accessibility'
import { MOTION_STEPS } from '../../world/motionPreference'
import {
  HEIGHT_OFFSETS,
  heightLabel,
  readHeightOffset,
  writeHeightOffset,
  type HeightOffset,
} from '../comfortPrefs'
import machineMeta from '../../../machines/neon-nova/machine.json?raw'
import { coarsePointer, touchCapable } from '../pointer'
import { hapticsSupported } from '../../input/touchHaptics'
import {
  AUDIO_GROUPS,
  AUDIO_LEVELS,
  readAudioPrefs,
  withAudioLevel,
  writeAudioLevel,
  writeAudioMuted,
  type AudioGroup,
  type AudioLevel,
  type AudioPrefs,
  withAudioMuted,
  withAudioCaptions,
  withAudioMaster,
  writeAudioCaptions,
  writeAudioMaster,
} from '../../audio/audioPrefs'
import { LANGUAGES, type Language, type MessageKey } from '../i18n'
import { useLocalisation } from '../i18n/useLanguage'
import { CURRENCIES, type Currency } from '../../ui/pricingCatalog'
import { RATING_CHOICES, readRatingChoice, writeRatingChoice } from '../ratingPref'
import type { RatingAllowed } from '@server/moderation'

/** The tiers a player may force. `xr` is a device, not a rung, and is never offered on a screen. */
const OFFERED_TIERS: readonly RenderTier[] = RENDER_TIERS.filter((tier) => tier !== 'xr')

/**
 * Theme, flashing and motion each map a stored value to a **key**, not to words.
 *
 * They were `Record<…, string>` tables of English. A lookup table is exactly where a translation
 * gets forgotten, because it does not look like copy — it looks like configuration.
 */
const THEME_KEYS = {
  system: 'settings.theme.system',
  light: 'settings.theme.light',
  dark: 'settings.theme.dark',
} as const

export function Settings({ theme }: { theme: ThemeController }): React.JSX.Element {
  const [choice, setChoice] = useState<ThemeChoice>(theme.choice)
  const { t, language, currency, setLanguage, setCurrency } = useLocalisation()
  const tier = renderTier()
  const [forced, setForced] = useState<RenderTier | undefined>(tier.override)
  const [motionHost] = useState(browserMotionHost)
  // 11 §6's age filter (M9-50). Read once: a stored preference does not change while the page that
  // writes it is open, and the button that changes it sets this in the same breath.
  const [rating, setRating] = useState<RatingAllowed>(readRatingChoice)
  // Whether this browser has already spent its one showing of the flipper zones (M3-33a).
  const [zonesSeen, setZonesSeen] = useState(hasSeenTouchZones)
  // Resolved once. A pointer does not become coarse while a settings page is open.
  //
  // `touchCapable`, not `coarsePointer`: the touch controls are attached whatever the primary
  // pointer is, so a touchscreen laptop can be played with a finger and would otherwise never be
  // shown how. The nudging control two fields up keeps `coarsePointer`, because *that* one is
  // about a device somebody is going to shake.
  const [touchDevice] = useState(touchCapable)
  // Whether there is a motor to switch off. An iPhone is never shown a control that does nothing:
  // Safari has never implemented `navigator.vibrate` and shows no sign of doing so.
  const [canBuzz] = useState(() => hapticsSupported())
  const [haptics, setHaptics] = useState(readHaptics)
  // Unanswered reads as off here, unlike on the machine where it reads as "ask". A settings page
  // has no third state to show: the control is a pair of choices and one of them is what the
  // machine is doing right now, which for a player who has never been asked is nothing.
  const [motion, setMotion] = useState<MotionChoice>(() => readMotionChoice() ?? 'off')
  const [refused, setRefused] = useState(false)
  const [kept, setKept] = useState<{ scores: number; replays: number; available: boolean } | undefined>(undefined)
  const [flashing, setFlashing] = useState<FlashingChoice>(readFlashingChoice)
  const [motionAmount, setMotionAmount] = useState(readMotionIntensity)
  const [height, setHeight] = useState<HeightOffset>(readHeightOffset)
  /**
   * Whether there is any getting-started progress to clear (G2-11).
   *
   * A button that clears nothing is a control that does nothing when it is pressed, which is what
   * the zone hint's own `disabled` above exists to avoid — and the disabled state is the honest one
   * here as well, because "show them again" over a strip that has never been shown is a promise
   * about something the player has not seen.
   */
  const [onboarding, setOnboarding] = useState(() => readOnboarding())
  const onboardingStarted = onboarding.size > 0
  // One piece of state for all five audio controls, because they are one stored shape and five
  // `useState`s over one shape is five chances for them to disagree about what is stored.
  const [audio, setAudio] = useState<AudioPrefs>(() => readAudioPrefs())

  useEffect(() => {
    let live = true
    void LocalHistory.open().then(async (history) => {
      const scores = await history.scores(MACHINE.machineId)
      const replays = await history.replays()
      if (live) setKept({ scores: scores.length, replays: replays.length, available: history.available })
    })
    return () => {
      live = false
    }
  }, [])

  /** Throw away everything this browser is keeping about your games. */
  const forget = (): void => {
    void LocalHistory.open().then(async (history) => {
      await history.forget(MACHINE.machineId)
      setKept({ scores: 0, replays: 0, available: history.available })
    })
  }

  /**
   * Move one audio control (07 §7).
   *
   * **Storage only, and the page says so.** There is no machine mounted on this route — the stage
   * unmounts and closes its `AudioContext` on the way here — so there is nothing to apply a level
   * to, and pretending otherwise would need an audio context this page has no gesture to start.
   * `MachineAudio` reads these at construction, so the next machine opens at the chosen levels.
   */
  const chooseLevel = (group: AudioGroup, level: AudioLevel): void => {
    writeAudioLevel(group, level)
    setAudio((prefs) => withAudioLevel(prefs, group, level))
  }

  /**
   * Move the master (07 §7's "plus a master volume").
   *
   * Stored and applied at the next mount, exactly like the four controls below it and for the
   * reason `chooseLevel` gives: this route has no machine and no `AudioContext` behind it, so
   * there is no mixer here to write to. `applyAudioPrefs` sets the master gain when one is built,
   * which is the same path the levels and the mute take.
   *
   * **The wrist row is the live one**, because that one *is* over a running machine —
   * `MachineRuntime.setAudioMaster` moves the gain node as well as storing the choice. Two
   * surfaces, one stored value, and neither of them a second copy of it.
   */
  const chooseMaster = (master: AudioLevel): void => {
    writeAudioMaster(master)
    setAudio((prefs) => withAudioMaster(prefs, master))
  }

  const chooseMuted = (muted: boolean): void => {
    writeAudioMuted(muted)
    setAudio((prefs) => withAudioMuted(prefs, muted))
  }

  /**
   * Callout captions (NFR-022, 06 §5).
   *
   * Its own field rather than a fifth level control, because it is not a volume: a caption is what
   * a player reads when the callout is inaudible, so it is deliberately independent of the mute
   * and of the `speech` level and stays legible with both of them off.
   */
  const chooseCaptions = (captions: boolean): void => {
    writeAudioCaptions(captions)
    setAudio((prefs) => withAudioCaptions(prefs, captions))
  }

  /** Turning it on may need the platform's permission; turning it off never does. */
  const chooseMotion = (next: MotionChoice): void => {
    setRefused(false)
    if (next === 'off') {
      writeMotionChoice('off')
      setMotion('off')
      return
    }
    // Unawaited before the call, so the click is still the user gesture iOS requires.
    void requestMotionPermission(motionHost).then((answer) => {
      if (answer !== 'granted') {
        setRefused(answer === 'denied')
        return
      }
      writeMotionChoice('on')
      setMotion('on')
    })
  }

  return (
    <div className="page">
      {/* S3-04: this page had no heading of its own — the top bar’s label was its h1. */}
      <h1>{t('route.title.settings')}</h1>
      {/*
        The way into the account surface (M4-09). It lives here rather than on a tab of its own
        because the bottom bar drops to three tabs on a narrow phone and "You" is one of the three:
        a fourth tab for an account would be a destination that disappears on the devices most
        likely to be signing in on the move. It is also the first card rather than the last,
        because a player who came to this page looking for their scores is looking for this.
      */}
      <div className="card">
        <div className="field">
          <span className="field__label">{t('settings.account')}</span>
          <span className="field__hint">{t('settings.account-hint')}</span>
          <Link className="button" to="/account">
            {t('settings.account-link')}
          </Link>
        </div>
        {/*
          The Machine Builder (M10-25), in the same card as the account and for the reason the
          comment above gives for that one.

          **This card is the destinations card, and "You" is one of the three tabs a narrow bar
          keeps.** `NAV_TABS` marks Build `essential: false` and `tabsFor` drops it at or below
          `NARROW_MAX_PX` — 400 CSS pixels, which is every phone in portrait — so M10-24's build
          card in the roster was the only way in on the devices most people are holding, and it sits
          below the whole roster and sinks further with every machine anybody publishes.

          **A row, not a badge.** The card says what the trade is in its own hint rather than wearing
          a price: nothing gates the route, and a settings page is not a place to sell anything.
        */}
        <div className="field">
          <span className="field__label">{t('settings.builder')}</span>
          <span className="field__hint">{t('settings.builder-hint')}</span>
          <Link className="button" to="/builder" title={t('builder.open-title')}>
            {t('builder.open')}
          </Link>
        </div>
      </div>

      {/*
        Language and currency (M10-19).

        **Two controls, not one, and they are deliberately not linked.** A French-speaking American
        is a real person and so is an English-speaking Canadian; a page that switched the currency
        because somebody chose French would be confidently wrong about both of them. What the
        language *does* decide is how an amount is written — `CA$121.00` against `121,00 $ CA` —
        which is the locale's business rather than the currency's.

        **First card after the account, because a reader who cannot read the page cannot find a
        control buried below three graphics settings.** Somebody who has landed here in the wrong
        language is looking for exactly this and has to be able to recognise it without reading it,
        which is why each language is named in its own tongue: `Français` is legible to a French
        reader on an English page in a way that `French` is not.
      */}
      <div className="card">
        <div className="field">
          <span className="field__label" id="language-label">
            {t('settings.language')}
          </span>
          <span className="field__hint">{t('settings.language-hint')}</span>
          <div className="choices" role="group" aria-labelledby="language-label">
            {LANGUAGES.map((option: Language) => (
              <button
                key={option}
                type="button"
                className="button"
                lang={option}
                title={t('settings.language-title', {
                  language: t(option === 'fr' ? 'settings.language.fr' : 'settings.language.en'),
                })}
                aria-pressed={language === option}
                onClick={() => setLanguage(option)}
              >
                {t(option === 'fr' ? 'settings.language.fr' : 'settings.language.en')}
              </button>
            ))}
          </div>
        </div>

        <div className="field">
          <span className="field__label" id="currency-label">
            {t('settings.currency')}
          </span>
          <span className="field__hint">{t('settings.currency-hint')}</span>
          <div className="choices" role="group" aria-labelledby="currency-label">
            {CURRENCIES.map((option: Currency) => (
              <button
                key={option}
                type="button"
                className="button"
                title={t('settings.currency-title', {
                  currency: t(option === 'CAD' ? 'settings.currency.CAD' : 'settings.currency.USD'),
                })}
                aria-pressed={currency === option}
                onClick={() => setCurrency(option)}
              >
                {t(option === 'CAD' ? 'settings.currency.CAD' : 'settings.currency.USD')}
              </button>
            ))}
          </div>
        </div>

        {/*
          11 §6's age filter (M9-50).

          **The viewer's half of a line that only ever had an author's half.** Every machine has
          declared a `contentRating` since the format was written and nothing anywhere read one, so
          the arcade showed everybody everything — including the reviewer's own function for
          deciding otherwise, which had no caller in the product at all.

          **Two choices and not three**, because the format allows two. And "Everything" is the
          default: this app is rated 13+ (01 §Positioning), so a viewer is already presumed to be a
          teenager or older and the other setting is a gentler arcade somebody asks for. Starting at
          "Everyone only" would be half the catalogue missing with nothing on screen saying why.

          Beside language and currency rather than in the comfort card: it is a statement about what
          the *arcade* contains, not about how the machine behaves once you are playing it.
        */}
        <div className="field">
          <span className="field__label" id="rating-label">
            {t('settings.rating')}
          </span>
          <span className="field__hint">{t('settings.rating-hint')}</span>
          <div className="choices" role="group" aria-labelledby="rating-label">
            {RATING_CHOICES.map((option) => (
              <button
                key={option}
                type="button"
                className="button"
                title={t('settings.rating-title', {
                  rating: t(option === 'everyone' ? 'settings.rating.everyone' : 'settings.rating.teen'),
                })}
                aria-pressed={rating === option}
                onClick={() => {
                  writeRatingChoice(option)
                  setRating(option)
                }}
              >
                {t(option === 'everyone' ? 'settings.rating.everyone' : 'settings.rating.teen')}
              </button>
            ))}
          </div>
          {/*
            Said out loud rather than left as a rule in a comment. A player who chooses the gentler
            arcade and then finds a machine missing from it deserves to know it was missing because
            its author never rated it, not because somebody judged it.
          */}
          <span className="field__hint">{t('settings.rating-note')}</span>
        </div>
      </div>

      {/*
        Sound (07 §7).

        **Its own card, above appearance.** 07 §7 asks for a control per bus plus a master and a
        mute-all; this is four controls over six buses, because the split that serves a mixing rule
        does not serve a player — nobody has ever wanted the bumpers louder than the drop targets.
        The argument for each grouping is at the top of `audioPrefs.ts`.

        **Named steps rather than faders**, like every other control on this page: a fader has a
        hundred positions, ninety-seven of which nobody can tell apart, and it is the one shape that
        cannot be operated from a keyboard without reading a number out loud.

        **Callouts are separate from effects because muting them costs information**, not just
        noise — they are how the machine says BALL SAVED. A player turning the volume down in a
        quiet room is making a different decision from one who does not want to be told things.
      */}
      <div className="card">
        <div className="field">
          <span className="field__label" id="audio-label">
            {t('settings.audio.master')}
          </span>
          <span className="field__hint">{t('settings.audio.master-hint')}</span>
          <div className="choices" role="group" aria-labelledby="audio-label">
            <button
              type="button"
              className="button"
              title={t('settings.audio-title.on')}
              aria-pressed={!audio.muted}
              onClick={() => chooseMuted(false)}
            >
              {t('settings.audio.on')}
            </button>
            <button
              type="button"
              className="button"
              title={t('settings.audio-title.off')}
              aria-pressed={audio.muted}
              onClick={() => chooseMuted(true)}
            >
              {t('settings.audio.off')}
            </button>
          </div>
        </div>

        {/*
          **Between the mute and the four buses, because that is the question it answers.** The
          field above asks whether there is any sound at all; this asks how much of it; the four
          below ask which parts. 07 §7 lists them in that order — "every bus has a slider, plus a
          master volume and a mute-all" — and it is also the order somebody reaches for them.

          Its own control rather than a fifth group, because it is not one: it multiplies the four
          rather than sitting beside them, so turning the machine down here does not lose where a
          player had put music against effects.
        */}
        <div className="field">
          <span className="field__label" id="audio-volume-label">
            {t('settings.audio.volume')}
          </span>
          <span className="field__hint">{t('settings.audio.volume-hint')}</span>
          <div className="choices" role="group" aria-labelledby="audio-volume-label">
            {AUDIO_LEVELS.map((level) => (
              <button
                key={level}
                type="button"
                className="button"
                title={t('settings.audio-volume-title', {
                  level: t(AUDIO_LEVEL_KEYS[level]).toLowerCase(),
                })}
                aria-pressed={audio.master === level}
                onClick={() => chooseMaster(level)}
              >
                {t(AUDIO_LEVEL_KEYS[level])}
              </button>
            ))}
          </div>
        </div>

        {AUDIO_GROUPS.map((group) => (
          <div className="field" key={group}>
            <span className="field__label" id={`audio-${group}-label`}>
              {t(AUDIO_GROUP_KEYS[group])}
            </span>
            <span className="field__hint">{t(AUDIO_GROUP_HINT_KEYS[group])}</span>
            <div className="choices" role="group" aria-labelledby={`audio-${group}-label`}>
              {AUDIO_LEVELS.map((level) => (
                <button
                  key={level}
                  type="button"
                  className="button"
                  title={t('settings.audio-level-title', {
                    group: t(AUDIO_GROUP_KEYS[group]).toLowerCase(),
                    level: t(AUDIO_LEVEL_KEYS[level]).toLowerCase(),
                  })}
                  aria-pressed={audio.levels[group] === level}
                  onClick={() => chooseLevel(group, level)}
                >
                  {t(AUDIO_LEVEL_KEYS[level])}
                </button>
              ))}
            </div>
          </div>
        ))}

        <div className="field">
          <span className="field__label" id="captions-label">
            {t('settings.audio.captions')}
          </span>
          <span className="field__hint">{t('settings.audio.captions-hint')}</span>
          <div className="choices" role="group" aria-labelledby="captions-label">
            <button
              type="button"
              className="button"
              title={t('settings.audio-title.captions-on')}
              aria-pressed={audio.captions}
              onClick={() => chooseCaptions(true)}
            >
              {t('settings.audio.on')}
            </button>
            <button
              type="button"
              className="button"
              title={t('settings.audio-title.captions-off')}
              aria-pressed={!audio.captions}
              onClick={() => chooseCaptions(false)}
            >
              {t('settings.audio.off')}
            </button>
          </div>
        </div>

        <span className="field__hint">{t('settings.audio-hint')}</span>
      </div>

      <div className="card">
        <div className="field">
          <span className="field__label" id="appearance-label">
            {t('settings.appearance')}
          </span>
          <span className="field__hint">{t('settings.appearance-hint')}</span>
          <div className="choices" role="group" aria-labelledby="appearance-label">
            {THEME_CHOICES.map((option) => (
              <button
                key={option}
                type="button"
                className="button"
                title={t('settings.theme-title', { theme: t(THEME_KEYS[option]).toLowerCase() })}
                aria-pressed={choice === option}
                onClick={() => {
                  theme.set(option)
                  setChoice(option)
                }}
              >
                {t(THEME_KEYS[option])}
              </button>
            ))}
          </div>
        </div>

        <div className="field">
          <span className="field__label" id="flashing-label">
            {t('settings.flashing')}
          </span>
          <span className="field__hint">{t('settings.flashing-hint')}</span>
          <div className="choices" role="group" aria-labelledby="flashing-label">
            {FLASHING_CHOICES.map((option) => (
              <button
                key={option}
                type="button"
                className="button"
                title={t(FLASHING_TITLE_KEYS[option])}
                aria-pressed={flashing === option}
                onClick={() => {
                  writeFlashingChoice(option)
                  setFlashing(option)
                }}
              >
                {t(FLASHING_KEYS[option])}
              </button>
            ))}
          </div>
        </div>

        <div className="field">
          <span className="field__label" id="motion-label">
            {t('settings.motion')}
          </span>
          <span className="field__hint">{t('settings.motion-hint')}</span>
          <div className="choices" role="group" aria-labelledby="motion-label">
            {MOTION_STEPS.map((option) => {
              const label = MOTION_KEYS[option] === undefined ? String(option) : t(MOTION_KEYS[option]!)
              return (
                <button
                  key={option}
                  type="button"
                  className="button"
                  title={t('settings.motion-title', { amount: label })}
                  aria-pressed={Math.abs(motionAmount - option) < 1e-9}
                  onClick={() => {
                    writeMotionIntensity(option)
                    setMotionAmount(option)
                  }}
                >
                  {label}
                </button>
              )
            })}
          </div>
        </div>

        {/*
          06 §6's "adjustable machine height, independent of scale" (G2-02, G2-07).

          **The same latch the wrist row uses**, so neither surface can display a value the other is
          not at. It is a *drop* rather than a height — how far below the eyeline the lockdown bar
          sits — because 06 §3.1 is explicit that the floor cannot be trusted, and it is the only
          control on this page that does nothing at all outside a headset. Shown anyway: a player
          setting up before they put a headset on is exactly who wants to find it here, and a
          control that appeared only in VR would be one nobody could be told about.
        */}
        <div className="field">
          <span className="field__label" id="height-label">
            {t('settings.height')}
          </span>
          <span className="field__hint">{t('settings.height-hint')}</span>
          <div className="choices" role="group" aria-labelledby="height-label">
            {HEIGHT_OFFSETS.map((option) => (
              <button
                key={option}
                type="button"
                className="button"
                title={t('settings.height-title', { position: heightLabel(option) })}
                aria-pressed={height === option}
                onClick={() => {
                  writeHeightOffset(option)
                  setHeight(option)
                }}
              >
                {t(HEIGHT_KEYS[option])}
              </button>
            ))}
          </div>
        </div>

        <div className="field">
          <span className="field__label" id="graphics-label">
            {t('settings.graphics')}
          </span>
          {/*
            `tier.describe()` is deliberately left untranslated: it is a render tier name and a
            source word — `high · calibrated` — which are identifiers the settings panel and the
            harness HUD share. Translating them would mean two vocabularies for one diagnostic.
          */}
          <span className="field__hint">{t('settings.graphics-hint', { describe: tier.describe() })}</span>
          <div className="choices" role="group" aria-labelledby="graphics-label">
            <button
              type="button"
              className="button"
              title={t('settings.graphics-automatic-title')}
              aria-pressed={forced === undefined}
              onClick={() => {
                tier.setOverride(undefined)
                setForced(undefined)
              }}
            >
              {t('settings.graphics.automatic')}
            </button>
            {OFFERED_TIERS.map((option) => (
              <button
                key={option}
                type="button"
                className="button"
                title={t('settings.graphics-tier-title', { tier: option })}
                aria-pressed={forced === option}
                onClick={() => {
                  tier.setOverride(option)
                  setForced(option)
                }}
              >
                {option}
              </button>
            ))}
          </div>
          <span className="field__hint">{t('settings.graphics-note')}</span>
        </div>

        {shouldOfferMotionNudge(motionHost, coarsePointer()) ? (
          <div className="field">
            <span className="field__label" id="nudge-label">
              {t('settings.nudging')}
            </span>
            <span className="field__hint">{t('settings.nudging-hint')}</span>
            <div className="choices" role="group" aria-labelledby="nudge-label">
              <button
                type="button"
                className="button"
                title={t('settings.nudging-title.both')}
                aria-pressed={motion === 'on'}
                onClick={() => chooseMotion('on')}
              >
                {t('settings.nudging.both')}
              </button>
              <button
                type="button"
                className="button"
                title={t('settings.nudging-title.flick')}
                aria-pressed={motion === 'off'}
                onClick={() => chooseMotion('off')}
              >
                {t('settings.nudging.flick')}
              </button>
            </div>
            {refused ? (
              <span className="field__hint" role="status">
                {t('settings.nudging-refused')}
              </span>
            ) : null}
            <span className="field__hint">{t('settings.nudging-note')}</span>
          </div>
        ) : null}

        {/*
          Bringing the flipper hint back (M3-33a).

          **08 §5.2 is deliberate about not drawing the zones as buttons** — "the moment there are
          two grey rectangles at the bottom of the screen it stops being a pinball machine and
          becomes an app with buttons" — which makes the one-ball glow the *only* thing that ever
          says where the flippers are. `firstRun.ts` called that "permanent" and meant it.

          Permanent is right for the player who just flipped a flipper and wrong for everybody
          else: the same person a month later, the friend they hand the phone to, or anybody whose
          hint was spent on a stray tap before the flipper-press fix. The motion-nudge control
          immediately above made exactly this concession for exactly this reason, and this one did
          not. Offered only on a touch device, because there is nothing to show on a mouse.
        */}
        {touchDevice ? (
          <div className="field">
            <span className="field__label" id="zones-label">
              {t('settings.touch')}
            </span>
            {/*
              Three keys rather than one, because the sentence has a `<strong>` in the middle of it.
              Splitting on the emphasised word is the honest way to keep markup inside a translated
              sentence: the alternative is a bundle that holds HTML, which is a bundle that can
              inject it.
            */}
            <span className="field__hint">
              {t('settings.touch-hint-1')} <strong>{t('settings.touch-hint-down')}</strong>{' '}
              {t('settings.touch-hint-2')}
            </span>
            <div className="choices" role="group" aria-labelledby="zones-label">
              <button
                type="button"
                className="button"
                title={t('settings.touch-title')}
                disabled={!zonesSeen}
                {...(zonesSeen ? {} : { 'aria-disabled': true })}
                onClick={() => {
                  forgetTouchZones()
                  setZonesSeen(false)
                }}
              >
                {zonesSeen ? t('settings.touch.show-again') : t('settings.touch.will-show')}
              </button>
            </div>
            <span className="field__hint">{t('settings.touch-note')}</span>
          </div>
        ) : null}

        {/*
          The getting-started strip (G2-11).

          **Clearable, like the zone hint above it and for the reason recorded there**: "permanently"
          was too strong once already. A player handing the machine to somebody else, or coming back
          after a month, is a player for whom the four steps are new again — and the cost of being
          wrong is only that a strip nobody needs appears on this page until it is dismissed — and
          since M8-77 this row is where the strip *lives*, not merely where it is cleared: it was
          under the machine, on the bottom of the phone the player is holding, which a player called
          prime real estate and was right about.

          Shown on every device, unlike the zone hint: the four steps are about the *game* rather
          than about a touch gesture, so they are as relevant to somebody with a keyboard.
        */}
        <div className="field">
          <span className="field__label">{t('settings.onboarding')}</span>
          <span className="field__hint">{t('settings.onboarding-hint')}</span>
          {/*
            **The progress itself, which had nowhere to be shown** (M8-77). The strip was drawn under
            the machine; a player asked for it off the machine page, and the row that clears it was
            already here offering to reset a progress nothing displayed. Here it costs a page nobody
            plays on rather than the bottom of the one they do.
          */}
          <OnboardingStrip progress={onboarding} />
          {/*
            **No `role="group"` on this one, unlike every other row's choices.** The strip above is
            a named group and it is called "Getting started"; so is this row's label. A group of one
            button, nested beside another group with the identical name, is a screen reader saying
            "Getting started, group" twice for two different things — measured on a phone the moment
            the strip moved here. A lone button needs no group, and its own title says what it does.
          */}
          <div className="choices">
            <button
              type="button"
              className="button"
              title={t('onboarding.forget-title')}
              disabled={onboardingStarted === false}
              {...(onboardingStarted ? {} : { 'aria-disabled': true })}
              onClick={() => {
                forgetOnboarding()
                setOnboarding(readOnboarding())
              }}
            >
              {t('onboarding.forget')}
            </button>
          </div>
        </div>

        {/*
          Haptics (M3-42).

          **On by default, which is the opposite of the motion offer above it.** That one asks the
          platform for a permission and can only be granted through a prompt, so it starts off and
          is offered. This needs no permission and is the feedback that makes a flipper feel like a
          flipper — a player who has to find a switch before the machine answers their thumb has
          already formed their opinion of it.

          It stays a control because a buzz is genuinely unwanted by some people: in a quiet room,
          on a desk, or for anybody who finds vibration unpleasant.
        */}
        {canBuzz ? (
          <div className="field">
            <span className="field__label" id="haptics-label">
              {t('settings.haptics')}
            </span>
            <span className="field__hint">{t('settings.haptics-hint')}</span>
            <div className="choices" role="group" aria-labelledby="haptics-label">
              <button
                type="button"
                className="button"
                title={t('settings.haptics-title.on')}
                aria-pressed={haptics}
                onClick={() => {
                  writeHaptics(true)
                  setHaptics(true)
                }}
              >
                {t('settings.haptics.on')}
              </button>
              <button
                type="button"
                className="button"
                title={t('settings.haptics-title.off')}
                aria-pressed={!haptics}
                onClick={() => {
                  writeHaptics(false)
                  setHaptics(false)
                }}
              >
                {t('settings.haptics.off')}
              </button>
            </div>
          </div>
        ) : null}

        {/*
          The headset handoff, rehomed (M5-01a).

          It used to be two buttons on the machine itself, pinned to the bottom of the playfield —
          which on a phone is directly over the flippers. On a device that cannot present a
          session they never entered anything anyway: both of them navigate here, to a QR code. So
          the offer lives on the page people already come to for "take this somewhere else",
          beside the account link, and the machine keeps its glass.
        */}
        <div className="field">
          <span className="field__label">{t('settings.headsets')}</span>
          <span className="field__hint">{t('settings.headsets-hint')}</span>
          <Link className="button" to="/headset">
            {t('settings.headsets-link')}
          </Link>
          <Link className="button" to="/account#headsets">
            {t('headsets.manage')}
          </Link>
        </div>

        <div className="field">
          <span className="field__label" id="kept-label">
            {t('settings.kept')}
          </span>
          {/*
            **Two whole sentences, one per plural form**, rather than a stem with an `s` appended.
            `${n} score${n === 1 ? '' : 's'}` is a rule about English that is wrong in most
            languages and was inlined here; French agrees differently, and a bundle that stores
            fragments cannot fix that at the call site.
          */}
          <span className="field__hint">
            {kept === undefined
              ? t('settings.kept-looking')
              : !kept.available
                ? t('settings.kept-unavailable')
                : kept.scores === 1
                  ? t('settings.kept-summary-one', { replays: kept.replays, max: MAX_REPLAYS })
                  : t('settings.kept-summary-many', {
                      scores: kept.scores,
                      replays: kept.replays,
                      max: MAX_REPLAYS,
                    })}
          </span>
          <div className="choices" role="group" aria-labelledby="kept-label">
            <button
              type="button"
              className="button"
              title={t('settings.kept-forget-title')}
              disabled={kept === undefined || !kept.available || kept.scores + kept.replays === 0}
              onClick={forget}
            >
              {t('settings.kept-forget')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

/** Each audio control's name and its explanation, as keys rather than as English. */
const AUDIO_GROUP_KEYS = {
  music: 'settings.audio.music',
  effects: 'settings.audio.effects',
  speech: 'settings.audio.speech',
  ambience: 'settings.audio.ambience',
} as const

const AUDIO_GROUP_HINT_KEYS = {
  music: 'settings.audio.music-hint',
  effects: 'settings.audio.effects-hint',
  speech: 'settings.audio.speech-hint',
  ambience: 'settings.audio.ambience-hint',
} as const

const AUDIO_LEVEL_KEYS = {
  full: 'settings.audio.level.full',
  low: 'settings.audio.level.low',
  off: 'settings.audio.level.off',
} as const

const FLASHING_KEYS = {
  system: 'settings.flashing.system',
  on: 'settings.flashing.on',
  off: 'settings.flashing.off',
} as const

const FLASHING_TITLE_KEYS = {
  system: 'settings.flashing-title.system',
  on: 'settings.flashing-title.on',
  off: 'settings.flashing-title.off',
} as const

/**
 * Words for the five positions the machine may sit at (06 §6).
 *
 * `MessageKey` as the value type, so an offset added to `HEIGHT_OFFSETS` without copy does not
 * compile — the same guard `MOTION_KEYS` below has and, unlike that one, with no `undefined` arm to
 * fall through, because this table is keyed by a closed union rather than by a number.
 */
const HEIGHT_KEYS: Readonly<Record<HeightOffset, MessageKey>> = {
  'much-lower': 'settings.height.much-lower',
  lower: 'settings.height.lower',
  level: 'settings.height.level',
  higher: 'settings.height.higher',
  'much-higher': 'settings.height.much-higher',
}

/** Words for the four positions `MOTION_STEPS` offers, so a player picks rather than typing. */
const MOTION_KEYS: Readonly<Record<number, 'settings.motion.full' | 'settings.motion.half' | 'settings.motion.hint' | 'settings.motion.none'>> = {
  1: 'settings.motion.full',
  0.5: 'settings.motion.half',
  0.25: 'settings.motion.hint',
  0: 'settings.motion.none',
}

/** The machine this page is talking about, read from the same bytes the stage is built from. */
const MACHINE = JSON.parse(machineMeta) as { machineId: string }
